// import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { AnnouncementsPage } from '@procore-oss/backstage-plugin-announcements';
import { AiAssistantPage } from './components/ai-assistant/AiAssistantPage';
import { AiCentralPage } from './components/ai-central';
// Add RAG AI import
import { RagModal } from '@roadiehq/rag-ai';

const HOSTNAME = typeof window !== 'undefined' ? window.location.hostname : '';
const IS_LOCALHOST =
  HOSTNAME === 'localhost' || HOSTNAME === '127.0.0.1' || HOSTNAME === '::1';

const EnvironmentBanner = () => {
  let envLabel = '';
  let bgColor = '';
  if (IS_LOCALHOST) {
    envLabel = 'LOCAL';
    bgColor = '#6a0dad';
  } else if (HOSTNAME.includes('.dv.')) {
    envLabel = 'DEV';
    bgColor = '#1565c0';
  } else if (HOSTNAME.includes('.ci.')) {
    envLabel = 'CI';
    bgColor = '#e65100';
  }

  if (!envLabel) return null;

  return (
    <div style={{
      background: bgColor,
      color: '#fff',
      textAlign: 'center',
      padding: '6px 0',
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      width: '100%',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9999,
    }}>
      {envLabel}
    </div>
  );
};

const AppSignInPage = (props: any) => {
  const providers: any[] = [
    {
      id: 'azure-auth-provider',
      title: 'Sign in using Microsoft',
      message: 'Sign in with your AvidXchange account',
      apiRef: microsoftAuthApiRef,
    },
  ];

  if (IS_LOCALHOST) {
    providers.push('guest');
  }

  return (
    <>
      <EnvironmentBanner />
      <SignInPage
        {...props}
        auto
        providers={providers}
      />
    </>
  );
};

const app = createApp({
  components: {
    SignInPage: AppSignInPage,
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/qeta" element={<QetaPage title="Questions" />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/ai-assistant" element={<AiAssistantPage />} />
    <Route path="/ai-central" element={<AiCentralPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <RagModal />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
